import { Capacitor } from "@capacitor/core";
import { APP_FLAVOR, type AppFlavor } from "./appFlavor.js";

/**
 * 빌드별 기능 분기 SSoT — 플랫폼/flavor 정적 게이트 + Firebase RemoteConfig 킬스위치.
 *
 * 정적 기본값: `isNative || flavor === "dev"` — 웹 live(itch.io) 빌드만 비활성.
 * 단, CrazyGames 웹 live는 광고 surface(dailyShop/houseAds/moduleFusion/banner)만 별도 활성화한다.
 * | 빌드                  | 일일상점/광고 | 던전/유물      |
 * |-----------------------|--------------|---------------|
 * | 웹 dev (vite dev)     | 켜짐         | 꺼짐(전역 임시) |
 * | 웹 live (build:web:live) | 꺼짐      | 꺼짐(전역 임시) |
 * | 모바일 (apk:dev/live) | 켜짐         | 꺼짐(전역 임시) |
 *
 * 차단 범위 2종:
 * - `GLOBAL_TEMP_DISABLED`(던전/유물): 플랫폼·flavor 무관 전 빌드 차단 — 아직 비공개 기능. 배열 비우면 전 빌드 복귀.
 * - `WEB_TEMP_DISABLED`(광고제거 IAP): 웹(dev·live)만 차단, 모바일은 노출. 배열 비우면 웹 복귀.
 *
 * RemoteConfig override는 **OFF 방향 전용** — 정적 기본이 켜진 기능을 출시 후 재배포 없이
 * 긴급 차단(킬스위치)할 수만 있고, 정적으로 꺼진 기능(웹 live)을 원격으로 켤 수는 없다
 * (미검증 코드 경로 개방 사고 방지). 결선은 main.ts resolveServices → registry "featureFlags".
 */
/**
 * 기능 키 SSoT — 신규 플래그 = 여기 1줄 append (interface 필드 + resolve 본문 2개소 편집 폐기).
 * - dailyShop: 일일상점 서브탭.
 * - dungeon: 던전 탭. (전 빌드 임시 차단 대상 — GLOBAL_TEMP_DISABLED)
 * - houseAds: 보상형 광고 Port 결선 (웹=하우스/CrazyGames / 네이티브=LevelPlay). false면 NoopRewardedAdProvider.
 * - moduleFusion: 모듈 융합 서브탭 (광고/다이아 비용·일일 리셋 의존 → 웹 live off).
 * - relic: 유물 탭. (전 빌드 임시 차단 대상 — GLOBAL_TEMP_DISABLED)
 * - iapRemoveAds: 광고제거 IAP 상품 노출(상점 행). (웹 임시 차단 대상 — WEB_TEMP_DISABLED. SaveData.adFree 보유 여부와 별개)
 * - banner: 모바일 상단 LevelPlay 배너 킬스위치. 실제 표시는 모바일 전용(createBannerAdProvider isNative 게이트 +
 *   shouldShowBanner) + 광고제거(adFree) 미보유 시. 이 플래그는 RemoteConfig OFF-only 긴급 차단용(웹은 isNative false라 무영향).
 */
/** 기능 키 전체 목록 — 신규 플래그 추가 시 여기에만 append하면 `FeatureKey` 유니온·`FeatureFlags` 레코드가 자동 파생된다. */
export const FEATURE_KEYS = ["dailyShop", "dungeon", "houseAds", "moduleFusion", "relic", "iapRemoveAds", "banner"] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];

export type FeatureFlags = Readonly<Record<FeatureKey, boolean>>;

/** RemoteConfig 등 외부에서 내려온 부분 override — 키가 없으면 정적 기본값 유지. */
export type FeatureOverrides = Partial<FeatureFlags>;

export type WebReleaseChannel = "default" | "crazygames";

const CRAZYGAMES_WEB_LIVE_ENABLED: readonly FeatureKey[] = [
  "dailyShop",
  "houseAds",
  "moduleFusion",
  "banner",
];

/**
 * `__DISABLED_FEATURES__`는 vite.config의 `define`으로 빌드 시점에 문자열 치환된다
 * (`VITE_DISABLE_FEATURES` env ← 미설정 시 vite.config 디폴트 "iapRemoveAds"). comma-separated.
 * 미주입(vitest 등 define 밖) 시 빈 문자열 폴백 — appFlavor.ts의 __APP_FLAVOR__와 동일 패턴.
 */
declare const __DISABLED_FEATURES__: string;

/**
 * `__DISABLED_FEATURES_ALL__`: 플랫폼·flavor 무관 전역 차단 목록(comma-separated). vite.config `define`이
 * `VITE_DISABLE_FEATURES_ALL` env로 빌드 시점 치환(미설정 시 디폴트 "dungeon,relic"). 미주입 시 빈 문자열 폴백.
 */
declare const __DISABLED_FEATURES_ALL__: string;

/**
 * 비활성 기능 목록 파싱 — 쉼표 구분 문자열을 유효한 `FeatureKey` 배열로 변환한다.
 * `FEATURE_KEYS`에 없는 토큰은 조용히 무시되므로 잘못된 env 값에도 안전하다.
 * `VITE_DISABLE_FEATURES` env → vite `define.__DISABLED_FEATURES__` → 이 함수로 파싱.
 * 순수 함수(단위테스트 대상).
 */
export function parseDisabledFeatures(raw: string): readonly FeatureKey[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is FeatureKey => (FEATURE_KEYS as readonly string[]).includes(s));
}

/**
 * 웹(dev·live 공통)에서만 임시 차단할 기능 키 목록 — `__DISABLED_FEATURES__` define을 파싱. 기본값 `["iapRemoveAds"]`.
 *
 * 모바일(네이티브)은 `resolveFeatureFlags`의 `isNative` 가드로 이 목록에 영향받지 않는다(모바일은 노출).
 * 전 빌드 차단(던전/유물)은 `GLOBAL_TEMP_DISABLED` 참조.
 *
 * 재활성/변경(게임 src 수정 없이): env `VITE_DISABLE_FEATURES=`(빈값) 또는 vite/vitest.config 디폴트 편집.
 */
export const WEB_TEMP_DISABLED: readonly FeatureKey[] = parseDisabledFeatures(
  typeof __DISABLED_FEATURES__ !== "undefined" ? __DISABLED_FEATURES__ : "",
);

/**
 * 플랫폼·flavor 무관 전 빌드(웹+모바일 dev/live) 차단 기능 키 목록 — `__DISABLED_FEATURES_ALL__` define을 파싱.
 * 기본값 `["dungeon","relic"]` (아직 비공개 기능 — 모바일에서도 숨김).
 *
 * 재활성: env `VITE_DISABLE_FEATURES_ALL=`(빈값 → 전 빌드 복귀) 또는 vite/vitest.config 디폴트 편집.
 */
export const GLOBAL_TEMP_DISABLED: readonly FeatureKey[] = parseDisabledFeatures(
  typeof __DISABLED_FEATURES_ALL__ !== "undefined" ? __DISABLED_FEATURES_ALL__ : "",
);

/**
 * 플랫폼(네이티브 여부) + flavor → 기능 플래그 레코드 도출. 순수 함수(단위테스트 대상).
 * override는 OFF-only 병합(`base && (override ?? true)`) — 정적으로 꺼진 기능을 원격으로 켤 수 없다.
 *
 * @param isNative  네이티브 플랫폼 여부(`Capacitor.isNativePlatform()`).
 * @param flavor    빌드 flavor (`"dev"` | `"live"`).
 * @param overrides RemoteConfig 등 외부에서 내려온 부분 override(OFF 방향 전용).
 * @param webDisabled 웹(비네이티브) 환경에서만 차단할 키 목록. 기본값 `WEB_TEMP_DISABLED`.
 *   테스트에서 `[]`를 주입하면 웹 임시 차단 없이 검증할 수 있다.
 * @param globalDisabled 플랫폼·flavor 무관 전 빌드 차단 키 목록. 기본값 `GLOBAL_TEMP_DISABLED`.
 *   테스트에서 `[]`를 주입하면 전역 차단 없이 검증할 수 있다.
 * @param webReleaseChannel 웹 live 특화 채널. `"crazygames"`면 광고 surface에 필요한 기능만 웹 live에서도 켠다.
 */
export function resolveFeatureFlags(
  isNative: boolean,
  flavor: AppFlavor,
  overrides: FeatureOverrides = {},
  webDisabled: readonly FeatureKey[] = WEB_TEMP_DISABLED,
  globalDisabled: readonly FeatureKey[] = GLOBAL_TEMP_DISABLED,
  webReleaseChannel: WebReleaseChannel = "default",
): FeatureFlags {
  return Object.fromEntries(
    FEATURE_KEYS.map((key) => {
      const crazyGamesWebLive =
        !isNative &&
        flavor === "live" &&
        webReleaseChannel === "crazygames" &&
        CRAZYGAMES_WEB_LIVE_ENABLED.includes(key);
      const base = isNative || flavor === "dev" || crazyGamesWebLive;
      // 전역 차단(던전/유물)은 모바일 포함 전 빌드, 웹 차단(광고제거 IAP)은 비네이티브만.
      const off = globalDisabled.includes(key) || (!isNative && webDisabled.includes(key));
      return [key, base && !off && (overrides[key] ?? true)];
    }),
  ) as Record<FeatureKey, boolean>;
}

/**
 * 현재 빌드 기능 플래그 — RemoteConfig 미반영 정적 기본값(동기 fallback).
 * RC override 반영분은 main.ts가 registry "featureFlags"로 주입하며, GameScene은
 * registry 값을 우선하고 미주입(테스트/AutoPlay 단독 부팅) 시 본 상수를 쓴다.
 */
export const FEATURES: FeatureFlags = resolveFeatureFlags(
  Capacitor.isNativePlatform(),
  APP_FLAVOR,
  {},
  WEB_TEMP_DISABLED,
  GLOBAL_TEMP_DISABLED,
  (import.meta.env["VITE_WEB_AD_PROVIDER"] as string | undefined) === "crazygames"
    ? "crazygames"
    : "default",
);
